/**
 * InlineGalleryEditor — édition inline d'une galerie de photos.
 *
 * En mode public (non admin) : ne rend rien (la galerie est rendue par le parent).
 * En mode admin : affiche une barre d'outils flottante en haut de la galerie avec :
 *   - Bouton "+ Ajouter une photo" (MediaPicker)
 *   - Sur survol de chaque image : boutons ↑ ↓ × et champ alt éditable
 *
 * `items` — array de `{src, alt}` (src = URL ou basename local)
 * `onChange(newItems)` — appelé à chaque modification (delegue au parent le save)
 * `slug` — slug de l'expérience (pour data-testid)
 */

import { useEffect, useState } from "react";
import { Plus, ChevronUp, ChevronDown, X, Pencil } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import MediaPicker from "./MediaPicker";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

function move(arr, from, to) {
  if (to < 0 || to >= arr.length) return arr;
  const copy = [...arr];
  const [x] = copy.splice(from, 1);
  copy.splice(to, 0, x);
  return copy;
}

// Convertit un basename uploaded (ex: "custom-xxx.png") en URL de rendu.
// Si `src` contient déjà un slash ou "http", on garde tel quel.
function resolveSrc(src) {
  if (!src) return "";
  if (src.startsWith("http") || src.startsWith("/")) return src;
  return `${BACKEND}/api/media/${encodeURIComponent(src)}`;
}

export default function InlineGalleryEditor({ items = [], onChange, slug = "", children }) {
  const { user } = useAuth();
  const isAdmin = !!user;
  const [open, setOpen] = useState(false);
  const [uploaded, setUploaded] = useState([]);
  const [editingIdx, setEditingIdx] = useState(null);
  const [selectIdx, setSelectIdx] = useState(null); // index à remplacer (null = ajouter)

  useEffect(() => {
    if (open) api.listMedia("refuge").then(setUploaded).catch(() => {});
  }, [open]);

  // Rendu public : simplement retourne le contenu enfant (galerie normale).
  if (!isAdmin) return children;

  function handleAdd(filename) {
    if (selectIdx !== null) {
      // Remplacement
      const next = items.map((it, i) => (i === selectIdx ? { ...it, src: filename } : it));
      onChange(next);
    } else {
      // Ajout à la fin
      onChange([...items, { src: filename, alt: "" }]);
    }
    setOpen(false);
    setSelectIdx(null);
  }
  function handleRemove(idx) {
    if (!confirm(`Retirer cette image de la galerie ?`)) return;
    onChange(items.filter((_, i) => i !== idx));
  }
  function handleMove(idx, dir) {
    onChange(move(items, idx, idx + dir));
  }
  function handleAltChange(idx, alt) {
    onChange(items.map((it, i) => (i === idx ? { ...it, alt } : it)));
  }

  return (
    <div className="relative" data-testid={`inline-gallery-editor-${slug}`}>
      {/* Barre d'outils admin en haut de la galerie */}
      <div className="mb-4 flex items-center gap-2 flex-wrap justify-center">
        <button
          type="button"
          onClick={() => { setSelectIdx(null); setOpen(true); }}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-refuge-ruby text-white text-xs font-body uppercase tracking-[0.15em] shadow-md hover:bg-refuge-ruby/90 transition-all"
          data-testid={`gallery-add-${slug}`}
        >
          <Plus size={14} strokeWidth={1.5} />
          Ajouter une photo
        </button>
        <span className="text-xs text-refuge-soft font-body italic">
          {items.length} photo{items.length > 1 ? "s" : ""} · admin uniquement
        </span>
      </div>

      {/* Wrapper autour de la galerie enfant + overlay hover par image */}
      <div className="relative">
        {children}
        {/* Overlay grid absolue au-dessus de la galerie rendue par le parent */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="max-w-6xl mx-auto px-6 h-full">
            <div className={items.length === 1 ? "max-w-3xl mx-auto" : "grid grid-cols-1 md:grid-cols-6 gap-4 md:gap-5"}>
              {items.map((it, i) => {
                const layout = items.length === 1 ? "" : (i === 0 ? "md:col-span-6" : "md:col-span-3");
                const ratio = items.length === 1
                  ? "aspect-[4/3]"
                  : (i === 0 ? "aspect-[16/9] md:aspect-[21/9]" : "aspect-[4/3]");
                return (
                  <div
                    key={i}
                    className={`${layout} ${ratio} relative pointer-events-auto group/gedit rounded-2xl`}
                    style={{ background: "transparent" }}
                  >
                    {/* Contrôles admin par image */}
                    <div className="absolute inset-0 opacity-0 group-hover/gedit:opacity-100 transition-opacity bg-black/40 rounded-2xl flex flex-col items-center justify-center gap-2">
                      <div className="flex gap-1.5">
                        <button
                          type="button"
                          onClick={() => handleMove(i, -1)}
                          disabled={i === 0}
                          className="w-9 h-9 rounded-full bg-white text-refuge-text hover:bg-refuge-ruby hover:text-white disabled:opacity-30 shadow-md flex items-center justify-center"
                          title="Monter"
                          data-testid={`gallery-up-${slug}-${i}`}
                        >
                          <ChevronUp size={16} />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleMove(i, 1)}
                          disabled={i === items.length - 1}
                          className="w-9 h-9 rounded-full bg-white text-refuge-text hover:bg-refuge-ruby hover:text-white disabled:opacity-30 shadow-md flex items-center justify-center"
                          title="Descendre"
                          data-testid={`gallery-down-${slug}-${i}`}
                        >
                          <ChevronDown size={16} />
                        </button>
                        <button
                          type="button"
                          onClick={() => { setSelectIdx(i); setOpen(true); }}
                          className="w-9 h-9 rounded-full bg-white text-refuge-text hover:bg-refuge-ruby hover:text-white shadow-md flex items-center justify-center"
                          title="Remplacer"
                          data-testid={`gallery-replace-${slug}-${i}`}
                        >
                          <Pencil size={14} />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleRemove(i)}
                          className="w-9 h-9 rounded-full bg-white text-refuge-ruby hover:bg-refuge-ruby hover:text-white shadow-md flex items-center justify-center"
                          title="Retirer"
                          data-testid={`gallery-remove-${slug}-${i}`}
                        >
                          <X size={16} />
                        </button>
                      </div>
                      {/* Champ ALT */}
                      {editingIdx === i ? (
                        <input
                          type="text"
                          autoFocus
                          value={it.alt || ""}
                          onChange={(e) => handleAltChange(i, e.target.value)}
                          onBlur={() => setEditingIdx(null)}
                          placeholder="Description de l'image (alt)"
                          className="px-3 py-1.5 rounded-full text-xs w-64 max-w-[80%] shadow-md border border-refuge-border"
                        />
                      ) : (
                        <button
                          type="button"
                          onClick={() => setEditingIdx(i)}
                          className="text-xs text-white bg-black/40 px-3 py-1 rounded-full backdrop-blur-sm hover:bg-black/60"
                        >
                          {it.alt ? `« ${it.alt.slice(0, 40)}${it.alt.length > 40 ? "…" : ""} »` : "Ajouter une description"}
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {open && (
        <MediaPicker
          uploaded={uploaded}
          onPick={handleAdd}
          onClose={() => { setOpen(false); setSelectIdx(null); }}
          onUploaded={async () => { const l = await api.listMedia("refuge"); setUploaded(l); }}
          site="refuge"
          accept="image/*"
        />
      )}
    </div>
  );
}

// Helper exporté pour normaliser une valeur `src` en URL rendue.
export { resolveSrc };
